// src/components/OtherIncome.js
import React, { useState, useEffect } from 'react';
import { DollarSign, HelpCircle, AlertCircle, ChevronDown, ChevronRight } from 'react-feather';

// Configuration for the "Less Common" Schedule 1 items
const ADVANCED_INCOME_ITEMS = [
  { key: 'netOperatingLoss', label: 'Net Operating Loss (8a)', description: 'Enter as a negative number usually.' },
  { key: 'cancellationOfDebt', label: 'Cancellation of Debt (8c)', description: 'Form 1099-C.' },
  { key: 'foreignEarnedIncomeExclusion', label: 'Foreign Earned Income Exclusion (8d)', description: 'Form 2555.' },
  { key: 'incomeFrom8853', label: 'Income from Archer MSA (8e)', description: 'Form 8853.' },
  { key: 'incomeFrom8889', label: 'Income from HSA (8f)', description: 'Form 8889.' },
  { key: 'alaskaDividends', label: 'Alaska Permanent Fund Dividends (8g)', description: '' },
  { key: 'hobbyIncome', label: 'Hobby Income (8j)', description: 'Activity not engaged in for profit.' },
  { key: 'stockOptions', label: 'Stock Options (8k)', description: 'Incentive stock options exercised.' },
  { key: 'personalPropertyRental', label: 'Rental of Personal Property (8l)', description: 'Not a business.' },
  { key: 'olympicPrizeMoney', label: 'Olympic/Paralympic Medals & Prizes (8m)', description: '' },
  { key: 'section951a', label: 'Section 951(a) Inclusion (8n)', description: '' },
  { key: 'section951Aa', label: 'Section 951A(a) Inclusion (8o)', description: '' },
  { key: 'excessBusinessLoss', label: 'Excess Business Loss Adjustment (8p)', description: 'Form 461.' },
  { key: 'ableDistributions', label: 'Taxable ABLE Distributions (8q)', description: '' },
  { key: 'scholarships', label: 'Taxable Scholarships (8r)', description: 'Not reported on W-2.' },
  { key: 'nonqualifiedPension', label: 'Nonqualified Deferred Compensation (8t)', description: '' },
  { key: 'incarceratedWages', label: 'Wages Earned While Incarcerated (8u)', description: '' },
  { key: 'digitalAssetOrdinaryIncome', label: 'Digital Assets Ordinary Income (8v)', description: 'Not capital gains.' },
];

function OtherIncome({ formData, totals, onDataChange }) {
  const [checkedItems, setCheckedItems] = useState({});
  
  // REMOVED: const [isExpanded, setIsExpanded] = useState(false);
  // REMOVED: useEffect for auto-expanding based on totals

  // ADD: Helper for Radio Buttons
  const handleToggleChange = (e) => {
    const { name, value } = e.target;
    onDataChange({
      target: { name, value: value === 'true', type: 'radio' }
    });
  };
  
  // Initialize checked items based on existing formData values
  useEffect(() => {
    const initial = {};
    ADVANCED_INCOME_ITEMS.forEach(item => {
      if (formData[item.key] && formData[item.key] !== 0) {
        initial[item.key] = true;
      }
    });
    setCheckedItems(initial);
  }, []); 

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    onDataChange({ target: { name, value } });
  };
  
  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <div className="header-title-with-help">
          <h2><DollarSign size={20} className="icon-orange" /> Other Miscellaneous Income</h2>
        </div>
      </div>

      {/* --- MOVED: DIGITAL ASSET QUESTION --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
        <div className="form-group checkbox-group">
            <input
                type="checkbox"
                id="digital-asset-checkbox"
                name="receivedDigitalAssets"
                checked={formData.receivedDigitalAssets || false}
                onChange={onDataChange}
            />
            <label htmlFor="digital-asset-checkbox" style={{ color: '#9a3412', fontWeight: '600', lineHeight: '1.4' }}>
                At any time during 2025, did you receive, sell, exchange, or otherwise dispose of any digital asset (cryptocurrency, NFTs, etc.)?
            </label>
        </div>
      </div>

      <div className="dependent-card">
        {/* --- Standard Items (Always Visible) --- */}
        <div className="form-group">
          <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
            <label>Taxable State Tax Refund</label>
            <div className="has-tooltip header-help-icon" data-tooltip="If you itemized deductions last year and received a state tax refund, enter the taxable amount here (from your 1099-G). If you took the Standard Deduction last year, leave this 0.">
              <HelpCircle size={16} />
            </div>
          </div>
          <input 
            type="number" 
            name="taxableStateRefund" 
            value={formData.taxableStateRefund ?? ''} 
            onChange={onDataChange} 
            placeholder="0" 
          />
        </div>

        <div className="form-group">
          <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
            <label>Unemployment Compensation</label>
            <div className="has-tooltip header-help-icon" data-tooltip="Enter the total unemployment compensation received. This is reported on Form 1099-G, Box 1.">
              <HelpCircle size={16} />
            </div>
          </div>
          <input type="number" name="unemploymentCompensation" value={formData.unemploymentCompensation ?? ''} onChange={onDataChange} placeholder="0" />
        </div>

        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Alimony Received</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter alimony received. Do not include child support.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="alimonyReceived" 
              value={formData.alimonyReceived ?? ''} 
              onChange={onDataChange} 
              placeholder="0" 
            />
          </div>
          
          {(parseFloat(formData.alimonyReceived) || 0) > 0 && (
            <div className="form-group">
              <label>Date of Divorce/Separation</label>
              <input 
                type="date" 
                name="divorceDate" 
                value={formData.divorceDate ?? ''} 
                onChange={onDataChange} 
              />
              <small>Required for Schedule 1, Line 2b</small>
            </div>
          )}
        </div>
        
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}><label>Union Strike or Lockout Benefits</label><div className="has-tooltip header-help-icon" data-tooltip="Benefits received from a union during a strike or lockout are generally considered taxable income."><HelpCircle size={16} /></div></div>
          <input type="number" name="strikeBenefits" value={formData.strikeBenefits ?? ''} onChange={onDataChange} placeholder="0" />
        </div>

        {/* --- NEW: Business & Farm Income Section --- */}
        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Other Gains/Losses (Form 4797)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the amount from Form 4797. This typically includes sale of business property.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="otherGainsLosses" 
              value={formData.otherGainsLosses ?? ''} 
              onChange={onDataChange} 
              placeholder="0" 
            />
            <small>Schedule 1, Line 4</small>
          </div>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Farm Income/Loss (Schedule F)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the net profit or loss from farming.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="farmIncome" 
              value={formData.farmIncome ?? ''} 
              onChange={onDataChange} 
              placeholder="0" 
            />
            <small>Schedule 1, Line 6</small>
          </div>
        </div>

        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>Gambling Winnings</label>
            <input type="number" name="gamblingWinnings" value={formData.gamblingWinnings ?? ''} onChange={onDataChange} placeholder="0" />
            <small>Form W-2G. Do not subtract losses here.</small>
          </div>
          <div className="form-group">
            <label>Jury Duty Pay</label>
            <input type="number" name="juryDutyPay" value={formData.juryDutyPay ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          {(parseFloat(formData.juryDutyPay) || 0) > 0 && (
              <div className="form-group" style={{ marginLeft: '1rem', borderLeft: '3px solid #f0e9e2ff', paddingLeft: '1rem', marginTop: '0.5rem' }}>
                  <label>Did you give this pay to your employer?</label>
                  <input 
                      type="number" 
                      name="juryDutyPayTurnedOver" 
                      value={formData.juryDutyPayTurnedOver ?? ''} 
                      onChange={onDataChange} 
                      placeholder="Enter amount given to employer" 
                  />
                  <small>This amount will be deducted on Schedule 1, Line 24a.</small>
              </div>
          )}
        </div>
        
        <div className="form-group" style={{ marginTop: '1rem' }}>
            <label>Prizes and Awards</label>
            <input type="number" name="prizesAndAwards" value={formData.prizesAndAwards ?? ''} onChange={onDataChange} placeholder="0" />
        </div>
<div className="form-group" style={{ 
    marginTop: '1rem', 
    backgroundColor: '#fff7ed', // CHANGED: from #f0f9ff (Blue) to Orange
    padding: '10px', 
    borderRadius: '5px', 
    border: '1px solid #fed7aa' // CHANGED: from #bae6fd (Blue) to Orange
}}>
    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
      {/* CHANGED: Label color from #0369a1 (Blue) to #9a3412 (Orange) */}
      <label style={{ color: '#9a3412' }}>Sale of Personal Items (Form 1099-K)</label>
      <div className="has-tooltip header-help-icon" data-tooltip="Enter the amount from Form 1099-K for personal items sold at a loss. This is reported at the top of Schedule 1 and is NOT taxed.">
        {/* CHANGED: Icon color from #0369a1 (Blue) to #9a3412 (Orange) */}
        <HelpCircle size={16} color="#9a3412" />
      </div>
    </div>
    <input 
      type="number" 
      name="saleOfPersonalItems1099K" 
      value={formData.saleOfPersonalItems1099K ?? ''} 
      onChange={onDataChange} 
      placeholder="0" 
    />
</div>

        <div className="form-section-divider"></div>

        {/* --- Line 4: IRA Distributions (UPDATED WITH TOOLTIPS) --- */}
        <div className="form-row">
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <label>Total IRA Distributions (4a)</label>
                <div className="has-tooltip header-help-icon" data-tooltip="Enter the total amount from Box 1 of Form 1099-R.">
                    <HelpCircle size={16} />
                </div>
            </div>
            <input type="number" name="totalIraDistributions" value={formData.totalIraDistributions ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <label>Taxable Amount (4b)</label>
                <div className="has-tooltip header-help-icon" data-tooltip="Enter the taxable amount from Box 2a of Form 1099-R. If Box 2a is empty, calculate the taxable portion based on your basis.">
                    <HelpCircle size={16} />
                </div>
            </div>
            <input type="number" name="taxableIraDistributions" value={formData.taxableIraDistributions ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
        </div>
        
        {/* Line 4c Checkboxes */}
        <div className="form-group" style={{ marginBottom: '1rem', backgroundColor: '#fcfaf8ff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#554433ff' }}>Line 4c Exceptions</label>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '5px' }}>
                <div className="checkbox-group">
                    <input type="checkbox" id="iraRollover" name="iraRollover" checked={formData.iraRollover || false} onChange={onDataChange} />
                    <label htmlFor="iraRollover">1. Rollover</label>
                </div>
                <div className="checkbox-group">
                    <input type="checkbox" id="iraQCD" name="iraQCD" checked={formData.iraQCD || false} onChange={onDataChange} />
                    <label htmlFor="iraQCD">2. QCD</label>
                </div>
                <div className="checkbox-group">
                    <input type="checkbox" id="iraHFD" name="iraHFD" checked={formData.iraHFD || false} onChange={onDataChange} />
                    <label htmlFor="iraHFD">3. Other (HFD, etc.)</label>
                </div>
            </div>
            
            {/* Conditional Input for Box 3 Code */}
            {formData.iraHFD && (
                <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '0.85rem' }}>Enter code for Box 3 (e.g., "HFD")</label>
                    <input 
                        type="text" 
                        name="iraBox3Code" 
                        value={formData.iraBox3Code || ''} 
                        onChange={handleTextChange} 
                        placeholder="HFD" 
                        style={{ width: '100px', marginTop: '5px' }}
                    />
                </div>
            )}
        </div>

        {/* --- Line 5: Pensions/Annuities --- */}
        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>Total Pensions/Annuities (5a)</label>
            <input type="number" name="totalPensionsAnnuities" value={formData.totalPensionsAnnuities ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          <div className="form-group">
            <label>Taxable Amount (5b)</label>
            <input type="number" name="taxablePensionsAnnuities" value={formData.taxablePensionsAnnuities ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
        </div>

        {/* Line 5c Checkboxes */}
        <div className="form-group" style={{ marginBottom: '1rem', backgroundColor: '#fcfaf8ff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#554433ff' }}>Line 5c Exceptions</label>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '5px' }}>
                <div className="checkbox-group">
                    <input type="checkbox" id="pensionRollover" name="pensionRollover" checked={formData.pensionRollover || false} onChange={onDataChange} />
                    <label htmlFor="pensionRollover">1. Rollover</label>
                </div>
                <div className="checkbox-group">
                    <input type="checkbox" id="pensionPSO" name="pensionPSO" checked={formData.pensionPSO || false} onChange={onDataChange} />
                    <label htmlFor="pensionPSO">2. PSO</label>
                </div>
                <div className="checkbox-group">
                    <input type="checkbox" id="pensionBox3" name="pensionBox3" checked={formData.pensionBox3 || false} onChange={onDataChange} />
                    <label htmlFor="pensionBox3">3. Other</label>
                </div>
            </div>

            {/* Conditional Input for Box 3 Code */}
            {formData.pensionBox3 && (
                <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '0.85rem' }}>Enter code for Box 3</label>
                    <input 
                        type="text" 
                        name="pensionBox3Code" 
                        value={formData.pensionBox3Code || ''} 
                        onChange={handleTextChange} 
                        placeholder="Code" 
                        style={{ width: '100px', marginTop: '5px' }}
                    />
                </div>
            )}
        </div>

        <div className="form-row" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>Total Social Security</label>
            <input type="number" name="totalSocialSecurityBenefits" value={formData.totalSocialSecurityBenefits ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          <div className="form-group">
            <label>Taxable Social Security</label>
            <input type="number" name="taxableSocialSecurityBenefits" value={formData.taxableSocialSecurityBenefits ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
        </div>
        
        <div className="form-group checkbox-group" style={{ marginTop: '0.5rem' }}>
          <input type="checkbox" id="lumpSumElection" name="lumpSumElection" checked={formData.lumpSumElection || false} onChange={onDataChange} />
          <label htmlFor="lumpSumElection">Lump Sum Election (Section 86(e))</label>
        </div>

        {/* Line 6d: MFS Lived Apart */}
        {formData.filingStatus === 'Married Filing Separately' && (
            <div className="form-group checkbox-group" style={{ marginTop: '0.5rem' }}>
                <input type="checkbox" id="livedApartEntireYear" name="livedApartEntireYear" checked={formData.livedApartEntireYear || false} onChange={onDataChange} />
                <label htmlFor="livedApartEntireYear">Lived apart from spouse entire year (Line 6d)</label>
            </div>
        )}

        <div className="form-section-divider"></div>

        {/* --- QUESTION CARD (Fixed Colors) --- */}
        <div className="form-group" style={{ 
            backgroundColor: '#fff7ed', // Changed from #f8fafc (Blue-Grey)
            padding: '1rem', 
            borderRadius: '0.5rem', 
            border: '1px solid #fed7aa' // Changed from #e2e8f0 (Blue-Grey)
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {/* Changed icon-blue to icon-orange */}
            <AlertCircle size={18} className="icon-orange" />
            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#9a3412', margin: 0 }}>
              Do you have other less common sources of income?
            </label>
          </div>
          <p style={{ fontSize: '0.9rem', color: '#c2410c', marginBottom: '1rem' }}>
            This includes Net Operating Loss, Cancellation of Debt, HSA Income, Stock Options, or Scholarships.
          </p>
          
          <div className="radio-group">
            <input 
              type="radio" id="sched1_add_no" name="hasSchedule1AdditionalIncome" 
              value="false" checked={formData.hasSchedule1AdditionalIncome === false} 
              onChange={handleToggleChange} 
            />
            <label htmlFor="sched1_add_no">No</label>

            <input 
              type="radio" id="sched1_add_yes" name="hasSchedule1AdditionalIncome" 
              value="true" checked={formData.hasSchedule1AdditionalIncome === true} 
              onChange={handleToggleChange} 
            />
            <label htmlFor="sched1_add_yes">Yes</label>
          </div>
        </div>

          {formData.hasSchedule1AdditionalIncome && (
            <div style={{ padding: '0 1rem 1rem 1rem', borderTop: '1px solid #f0e9e2ff' }}>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '1rem' }}>
                Select any additional income sources that apply to you. These will appear on <strong>Schedule 1, Part I</strong>.
              </p>
              
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
                {ADVANCED_INCOME_ITEMS.map((item) => {
                  const isChecked = checkedItems[item.key] || false;
                  
                  return (
                    <div key={item.key} style={{ padding: '0.5rem', borderBottom: '1px solid #f9f5f1ff' }}>
                      <div className="form-group checkbox-group" style={{ marginBottom: '0.5rem' }}>
                        <input 
                          type="checkbox" 
                          id={`check-${item.key}`} 
                          checked={isChecked} 
                          onChange={(e) => {
                            setCheckedItems({
                              ...checkedItems,
                              [item.key]: e.target.checked
                            });
                            // If unchecked, clear the value
                            if (!e.target.checked) {
                              onDataChange({ target: { name: item.key, value: 0 } });
                            }
                          }}
                        />
                        <label htmlFor={`check-${item.key}`} style={{ fontWeight: '500' }}>{item.label}</label>
                      </div>
                      
                      {/* Only show input if the checkbox is checked */}
                      {isChecked && (
                        <div style={{ marginLeft: '1.8rem' }}>
                          <input 
                            type="number" 
                            name={item.key} 
                            value={formData[item.key] ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0"
                            autoFocus
                          />
                          {item.description && <small style={{ display: 'block', marginTop: '0.25rem' }}>{item.description}</small>}
                          {/* Conditional Input for Line 24b */}
                          {item.key === 'personalPropertyRental' && (
                              <div style={{ marginTop: '0.5rem' }}>
                                  <label style={{ fontSize: '0.9rem' }}>Related Expenses (Line 24b)</label>
                                  <input 
                                      type="number" 
                                      name="personalPropertyRentalExpenses" 
                                      value={formData.personalPropertyRentalExpenses ?? ''} 
                                      onChange={onDataChange} 
                                      placeholder="0" 
                                  />
                              </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>
  );
}

export default OtherIncome;